// src/utils/topstepxParser.js
import Papa from "papaparse";

// Futures contract specifications
const FUTURES_SPECS = {
  ES: { pointValue: 50.0, tickSize: 0.25, tickValue: 12.5 }, // S&P 500
  MES: { pointValue: 5.0, tickSize: 0.25, tickValue: 1.25 }, // Micro S&P 500
  NQ: { pointValue: 20.0, tickSize: 0.25, tickValue: 5.0 }, // Nasdaq 100
  MNQ: { pointValue: 2.0, tickSize: 0.25, tickValue: 0.5 }, // Micro Nasdaq 100
  RTY: { pointValue: 50.0, tickSize: 0.1, tickValue: 5.0 }, // Russell 2000
  M2K: { pointValue: 5.0, tickSize: 0.1, tickValue: 0.5 }, // Micro Russell 2000
  YM: { pointValue: 5.0, tickSize: 1.0, tickValue: 5.0 }, // Dow Jones
  MYM: { pointValue: 0.5, tickSize: 1.0, tickValue: 0.5 }, // Micro Dow Jones
  CL: { pointValue: 1000.0, tickSize: 0.01, tickValue: 10.0 }, // Crude Oil
  GC: { pointValue: 100.0, tickSize: 0.1, tickValue: 10.0 }, // Gold
  SI: { pointValue: 5000.0, tickSize: 0.005, tickValue: 25.0 }, // Silver
  ZB: { pointValue: 1000.0, tickSize: 0.03125, tickValue: 31.25 }, // 30-Year Treasury Bond
  ZN: { pointValue: 1000.0, tickSize: 0.015625, tickValue: 15.625 }, // 10-Year Treasury Note
  "6E": { pointValue: 125000.0, tickSize: 0.00005, tickValue: 6.25 }, // Euro FX
};

// Extract the base symbol from contract name (e.g., "ESH5" -> "ES")
const getBaseSymbol = (contractName) => {
  // Match common futures prefixes
  // ES, MES, NQ, MNQ, RTY, M2K, YM, MYM, CL, GC, SI, ZB, ZN, 6E, etc.
  const regex = /^([A-Z0-9]{2,4})/;
  const match = contractName.match(regex);

  if (match) {
    const baseSymbol = match[1];
    // If this is a recognized futures contract, return it
    if (FUTURES_SPECS[baseSymbol]) {
      return baseSymbol;
    }

    // Special cases for contracts with numbers in them
    if (baseSymbol === "6E") return "6E";

    // More specific matching for contracts like NQH5, ESH5 (strip month codes and year)
    const symbolOnly = baseSymbol.replace(/[A-Z]\d$/, "");
    if (FUTURES_SPECS[symbolOnly]) {
      return symbolOnly;
    }
  }

  // If no match or not recognized, return null
  return null;
};

/**
 * Parse TopStepX CSV data into standardized trade objects
 * @param {string} csvContent - The CSV content as a string
 * @returns {Object} Object containing trades array, account balance, and daily P&L
 */
export const parseTopStepXCSV = (csvContent) => {
  console.log("Starting TopStepX CSV parse");

  // Parse the CSV with headers
  const results = Papa.parse(csvContent, {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: true, // Convert numbers to numbers
  });

  console.log("CSV parsing complete with", results.data.length, "rows");

  if (results.errors.length > 0) {
    console.error("CSV parsing errors:", results.errors);
  }

  // Process the parsed data
  const tradesByContract = {};
  const completedTrades = [];
  let accountBalance = 0;
  let dailyPnL = 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Group trades by order pairs (Opening and Closing)
  results.data.forEach((row) => {
    // Skip rows that aren't filled orders
    if (!row.Status || row.Status !== "Filled") return;

    // Get the necessary data from the row
    const contractName = row.ContractName;
    const side = row.Side; // Ask (Sell) or Bid (Buy)
    const price = row.ExecutePrice;
    const size = row.Size;
    const disposition = row.PositionDisposition; // Opening or Closing

    // Parse the date information
    const tradeDay = row.TradeDay || "";
    const filledAt = row.FilledAt || "";

    // Make sure we have all the required data
    if (!contractName || !side || !price || !size || !disposition) {
      console.log("Skipping incomplete row:", row);
      return;
    }

    // Create a trade object
    const trade = {
      id: row.Id,
      contractName,
      side,
      price,
      size,
      disposition,
      tradeDay,
      filledAt,
      accountName: row.AccountName,
    };

    // Group trades by contract
    if (!tradesByContract[contractName]) {
      tradesByContract[contractName] = [];
    }

    tradesByContract[contractName].push(trade);
  });

  // Process each contract's trades to match Opening and Closing positions
  Object.keys(tradesByContract).forEach((contractName) => {
    const trades = tradesByContract[contractName];

    // Skip if there are fewer than 2 trades (need at least one opening and one closing)
    if (trades.length < 2) return;

    // Get the base symbol for this contract
    const baseSymbol = getBaseSymbol(contractName);
    const pointValue =
      baseSymbol && FUTURES_SPECS[baseSymbol]
        ? FUTURES_SPECS[baseSymbol].pointValue
        : 50.0; // Default to ES point value if unknown

    // Find opening and closing pairs
    const openingTrades = trades.filter((t) => t.disposition === "Opening");
    const closingTrades = trades.filter((t) => t.disposition === "Closing");

    // Match pairs based on side (opening Buy/Bid should match with closing Sell/Ask and vice versa)
    openingTrades.forEach((opening) => {
      const matchingClosingIndex = closingTrades.findIndex(
        (closing) =>
          (opening.side === "Bid" && closing.side === "Ask") ||
          (opening.side === "Ask" && closing.side === "Bid")
      );

      if (matchingClosingIndex !== -1) {
        const closing = closingTrades[matchingClosingIndex];

        // Remove the matched closing trade so it's not reused
        closingTrades.splice(matchingClosingIndex, 1);

        // Determine if this was a long or short trade
        const isLong = opening.side === "Bid"; // Bid = Buy = Long position

        // Get entry and exit prices
        const entryPrice = parseFloat(opening.price);
        const exitPrice = parseFloat(closing.price);
        const quantity = parseInt(opening.size);

        // Calculate P&L based on direction and contract specifications
        let pnl;
        if (isLong) {
          // Long: exit price - entry price
          pnl = (exitPrice - entryPrice) * quantity * pointValue;
        } else {
          // Short: entry price - exit price
          pnl = (entryPrice - exitPrice) * quantity * pointValue;
        }

        // Check if trade is from today for daily P&L calculation
        const tradeDate = new Date(opening.tradeDay || opening.filledAt);
        const isToday = tradeDate.toDateString() === today.toDateString();

        if (isToday) {
          dailyPnL += pnl;
        }

        // Create the completed trade object
        const completedTrade = {
          date: new Date(opening.filledAt).toISOString(),
          symbol: contractName,
          direction: isLong ? "LONG" : "SHORT",
          entryPrice: parseFloat(entryPrice),
          exitPrice: parseFloat(exitPrice),
          quantity: parseInt(quantity),
          pnl: parseFloat(pnl.toFixed(2)),
          broker: "topstepx",
          status: pnl >= 0 ? "WIN" : "LOSS",
          commission: 0, // TopStepX may not include commission info
          notes: `Account: ${opening.accountName}`,
          tags: [],
          isFutures: true,
          contractMultiplier: parseFloat(pointValue),
          baseSymbol: baseSymbol || contractName.substring(0, 2),
          brokerType: "futures",
          timestamp: new Date().toISOString(), // Add timestamp for sorting
        };

        completedTrades.push(completedTrade);
      }
    });
  });

  // Set a default account balance for funded accounts
  accountBalance = 0; // Default to 0, will be configurable in settings

  // Sort trades by date (most recent first)
  completedTrades.sort((a, b) => new Date(b.date) - new Date(a.date));

  console.log(`Parsed ${completedTrades.length} completed trades`);

  return {
    trades: completedTrades,
    accountBalance,
    dailyPnL,
    brokerType: "futures",
  };
};

/**
 * Validate if a CSV file is in TopStepX format
 * @param {string} csvContent - The CSV content as a string
 * @returns {Object} Validation result with isValid flag and message
 */
export const validateTopStepXFormat = (csvContent) => {
  const results = Papa.parse(csvContent, { header: true });

  // Check for required columns based on the sample TopStepX CSV
  const requiredColumns = [
    "ContractName",
    "Status",
    "Size",
    "Side",
    "FilledAt",
    "ExecutePrice",
    "PositionDisposition",
  ];

  const headers = results.meta.fields || [];

  // Log the headers found in the CSV for debugging
  console.log("CSV Headers:", headers);

  // Check if minimum required columns are present
  const hasRequiredColumns = requiredColumns.every((col) =>
    headers.includes(col)
  );

  // Alternative check - if the CSV has Id, AccountName, ContractName, and Status,
  // it's probably a TopStepX export even if other column names differ
  const hasAlternativeColumns =
    headers.includes("Id") &&
    headers.includes("AccountName") &&
    headers.includes("ContractName") &&
    headers.includes("Status");

  const isValid = hasRequiredColumns || hasAlternativeColumns;

  return {
    isValid,
    message: isValid
      ? "Valid TopStepX format"
      : "Invalid file format. Please upload a TopStepX order export file.",
  };
};
